import React, { useState } from 'react';
import {
  Shield,
  UserCheck,
  ShoppingCart,
  ArrowLeft,
  Stethoscope,
  Settings,
  Check,
  X,
  Save,
  Users,
  Map,
  Eye,
  Paperclip,
  Loader2,
  Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';
import UserMenu from '../components/UserMenu';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../components/ui/dialog';

/**
 * Staff home screen — a launcher of "application" cards (Surgeons DB,
 * Purchasing, Population Explorer, Eye Exam, Zammad Attachments), each
 * navigateTo()'ing to its view.
 *
 * The launcher is permission-aware: cards are filtered by
 * can(appId, 'view') from AuthContext, where the id is the backend app
 * name (services/permissions.py APPS). Filtering is convenience only —
 * the same check is enforced server-side on every API call.
 *
 * The "Role Permissions" item in the user menu (administrators only)
 * opens a live matrix editor backed by GET/PUT /api/role-permissions.
 * Every role shown is real: the administrator row is rendered locked —
 * all capabilities on, inputs disabled — because the backend makes
 * administrator immutable by construction so an admin can never be
 * locked out. Capabilities an app does not support (per the server's
 * `supported` map) render disabled rather than hidden, so the shape of
 * the model stays visible.
 *
 * The per-app `stats` in `applications` are hardcoded placeholders, not
 * live counts.
 */
const StaffPortal = ({ navigateTo }) => {
  const { user, can, isAdministrator, permissionsLoading } = useAuth();
  const [showSettings, setShowSettings] = useState(false);

  // ── Live permission matrix state ─────────────────────────────────────
  // `matrix` is the server response (roles/apps/capabilities/supported/
  // clinical_config); `draft` is the editable copy keyed by role name;
  // `baseline` is the same grids as last fetched, for dirty detection.
  const [matrix, setMatrix]         = useState(null);
  const [draft, setDraft]           = useState(null);
  const [baseline, setBaseline]     = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError]     = useState(null);
  const [activeRoleTab, setActiveRoleTab] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState(null);
  const [saveOk, setSaveOk]         = useState(false);

  const applications = [
    {
      id: 'surgeons',
      title: 'Veterinary Surgeons Database',
      description: 'Access and manage veterinary surgeon profiles and credentials',
      iconName: 'UserCheck',
      color: 'green',
      view: 'surgeons-database',
      stats: { active: 23, onCall: 8, total: 31 }
    },
    {
      id: 'purchasing',
      title: 'Purchasing Control',
      description: 'Manage invoices, purchase orders, inventory and stock levels',
      iconName: 'ShoppingCart',
      color: 'purple',
      view: 'purchasing',
      stats: { orders: 8, pending: 3, total: 11 }
    },
    {
      id: 'population',
      title: 'UK Population Explorer',
      description: 'Explore UK Census 2021 population data by location and socioeconomic class',
      iconName: 'Map',
      color: 'teal',
      view: 'uk-population',
      stats: { total: '67M+', postcodes: '2700+', level: 'Output Area' }
    },
    {
      id: 'eye-exam',
      title: 'Eye Examination Chart',
      description: 'Veterinary ophthalmology exam form with drawable diagrams',
      iconName: 'Eye',
      color: 'indigo',
      view: 'eye-exam',
      stats: { diagrams: 5, modality: 'OPT' }
    },
    {
      id: 'zammad-attachments',
      title: 'Ticket Attachments',
      description: 'Download all attachments from a Zammad support ticket as individual files or a ZIP bundle',
      iconName: 'Paperclip',
      color: 'orange',
      view: 'zammad-attachments',
      // The permission app this card is gated by — usually the same as
      // `id`, but FreeScout deliberately shares 'zammad-attachments':
      // one matrix row governs both ticket systems (product decision).
      permApp: 'zammad-attachments',
      stats: { total: 'Zammad', source: 'support.optivet.com', type: 'Ticket Proxy' }
    },
    {
      id: 'freescout-attachments',
      title: 'FreeScout Attachments',
      description: 'Download all attachments from a FreeScout conversation as individual files or a ZIP bundle',
      iconName: 'Paperclip',
      color: 'rose',
      view: 'freescout-attachments',
      permApp: 'zammad-attachments',
      stats: { total: 'FreeScout', source: 'supportold.optivet.com', type: 'Ticket Proxy' }
    }
  ];

  // Only applications the current user's role grid permits are shown.
  // Enforcement is still server-side — a hidden card would only save a
  // 403. Cards key off their PERMISSION app (permApp), not their view
  // id, so the two ticket-attachment cards share one rule.
  const visibleApplications = applications.filter(
    app => can(app.permApp || app.id, 'view')
  );

  // Per-app metadata (title/icon) for the matrix rows, keyed by
  // PERMISSION app id — the server returns app ids like
  // 'zammad-attachments', never view ids like 'freescout-attachments',
  // so the new card cannot create a phantom matrix row. Only canonical
  // cards (permApp === id) feed this map: two cards sharing a permission
  // key would otherwise collide in fromEntries and the LAST one would
  // silently win, corrupting the row's title/colour/description.
  const appMeta = Object.fromEntries(
    applications
      .filter(a => (a.permApp || a.id) === a.id)
      .map(a => [a.permApp || a.id, a])
  );
  // The shared row must tell an admin one tick grants BOTH systems.
  appMeta['zammad-attachments'] = {
    ...appMeta['zammad-attachments'],
    title: 'Ticket Attachments (Zammad + FreeScout)',
  };

  const getIcon = (iconName) => {
    const icons = {
      Shield,
      UserCheck,
      ShoppingCart,
      Map,
      Eye,
      Paperclip
    };
    return icons[iconName] || Shield;
  };

  // ── Open the matrix: fetch the live state from the server ────────────
  const openSettings = async () => {
    setShowSettings(true);
    setMatrixLoading(true);
    setMatrixError(null);
    setSaveError(null);
    setSaveOk(false);
    try {
      const res = await apiFetch('/api/role-permissions');
      if (!res || !res.ok) throw new Error(`HTTP ${res?.status}`);
      const data = await res.json();
      const grids = {};
      data.roles.forEach(r => { grids[r.role] = r.permissions; });
      setMatrix(data);
      setDraft(structuredClone(grids));
      setBaseline(structuredClone(grids));
      setActiveRoleTab(data.roles[0]?.role || null);
    } catch (err) {
      setMatrixError('Could not load role permissions. Please try again.');
    } finally {
      setMatrixLoading(false);
    }
  };

  const togglePermission = (role, appId, cap) => {
    setDraft(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [appId]: {
          ...prev[role][appId],
          [cap]: !prev[role][appId]?.[cap],
        }
      }
    }));
    setSaveOk(false);
  };

  const roleIsDirty = (role) =>
    JSON.stringify(draft?.[role]) !== JSON.stringify(baseline?.[role]);

  // ── Save: one PUT per changed role (the endpoint is per-role). ───────
  // On any failure the dialog stays open and the failed roles are named,
  // so a partial save is never silently lost.
  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveOk(false);
    const changed = (matrix?.roles || [])
      .map(r => r.role)
      .filter(role => role !== 'administrator' && roleIsDirty(role));
    const failures = [];
    for (const role of changed) {
      try {
        const res = await apiFetch('/api/role-permissions', {
          method: 'PUT',
          body: JSON.stringify({ role, permissions: draft[role] }),
        });
        if (!res || !res.ok) {
          const detail = res ? await res.json().catch(() => ({})) : {};
          failures.push(`${role}: ${detail.detail || `HTTP ${res?.status}`}`);
        }
      } catch (err) {
        failures.push(`${role}: network error`);
      }
    }
    if (failures.length) {
      setSaveError(`Could not save ${failures.join('; ')}`);
    } else {
      setBaseline(structuredClone(draft));
      setSaveOk(true);
    }
    setSaving(false);
  };

  const activeRole = matrix?.roles.find(r => r.role === activeRoleTab);
  const activeEditable = !!activeRole?.editable;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <Stethoscope className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Staff Portal</h1>
                <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateTo('home')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </button>
              {/* Role Permissions is administrator-only — the backend
                  enforces it; we simply don't offer the item otherwise. */}
              <UserMenu
                navigateTo={navigateTo}
                extraItems={isAdministrator
                  ? [{ id: 'permissions', label: 'Role Permissions', icon: Shield, onClick: openSettings }]
                  : []}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Gateway Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Applications Gateway</h2>
          <p className="text-xl text-gray-600">Access the integrated applications for comprehensive practice management</p>
        </div>

        {/* Applications Grid — filtered by the user's role permissions */}
        {permissionsLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        ) : visibleApplications.length === 0 ? (
          <div className="max-w-md mx-auto text-center bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-10">
            <Lock className="h-10 w-10 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications available</h3>
            <p className="text-sm text-gray-600">
              Your role does not currently grant access to any application.
              Please contact an administrator if you believe this is wrong.
            </p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {visibleApplications.map((app) => (
            <div
              key={app.id}
              onClick={() => navigateTo(app.view)}
              className="group block cursor-pointer h-full"
            >
              <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 hover:scale-105 h-full flex flex-col">
                <div className="flex flex-col items-center text-center h-full">
                  <div className={`w-20 h-20 rounded-full bg-${app.color}-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    {React.createElement(getIcon(app.iconName), { className: `h-10 w-10 text-${app.color}-600` })}
                  </div>
                  <div className="mt-6 min-h-[8.5rem] flex flex-col justify-start">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{app.description}</p>
                  </div>
                  <div className="w-full space-y-3 min-h-[7.5rem] mt-6">
                    <div className="flex justify-between text-lg">
                      <span className="text-gray-500">Total</span>
                      <span className="font-bold text-gray-900">{app.stats.total}</span>
                    </div>
                    {app.id === 'surgeons' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Active</span>
                          <span className="font-medium text-green-600">{app.stats.active}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">On Call</span>
                          <span className="font-medium text-blue-600">{app.stats.onCall}</span>
                        </div>
                      </>
                    )}
                    {app.id === 'purchasing' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Orders</span>
                          <span className="font-medium text-blue-600">{app.stats.orders}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Pending</span>
                          <span className="font-medium text-yellow-600">{app.stats.pending}</span>
                        </div>
                      </>
                    )}
                    {app.id === 'zammad-attachments' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Server</span>
                          <span className="font-medium text-orange-600">{app.stats.source}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Type</span>
                          <span className="font-medium text-gray-600">{app.stats.type}</span>
                        </div>
                      </>
                    )}
                    {app.id === 'freescout-attachments' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Server</span>
                          <span className="font-medium text-rose-600">{app.stats.source}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Type</span>
                          <span className="font-medium text-gray-600">{app.stats.type}</span>
                        </div>
                      </>
                    )}
                    {app.id === 'population' && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Postcodes</span>
                          <span className="font-medium text-teal-600">{app.stats.postcodes}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Resolution</span>
                          <span className="font-medium text-teal-600">{app.stats.level}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="w-full mt-auto pt-6">
                    <div className={`w-full py-3 px-6 bg-${app.color}-600 hover:bg-${app.color}-700 text-white rounded-lg font-semibold transition-colors duration-200 text-center`}>
                      Open Application
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </main>

      {/* Role Permissions matrix — administrator only, live server data */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col overflow-hidden p-0">
          <DialogTitle className="sr-only">Access Control Settings</DialogTitle>
          {/* Modal Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Settings className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Access Control Settings</h2>
                <p className="text-sm text-gray-500">
                  Configure application permissions by staff role. Changes are
                  enforced by the API and take effect within 30 seconds.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowSettings(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {matrixLoading ? (
            <div className="flex-1 flex items-center justify-center p-16">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
          ) : matrixError ? (
            <div className="flex-1 flex items-center justify-center p-16">
              <p className="text-sm text-red-600">{matrixError}</p>
            </div>
          ) : matrix && (
            <>
              {/* Role Tabs */}
              <div className="flex border-b border-gray-200 px-6 shrink-0 overflow-x-auto">
                {matrix.roles.map(role => (
                  <button
                    key={role.role}
                    onClick={() => setActiveRoleTab(role.role)}
                    className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeRoleTab === role.role
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    <span>{role.label}</span>
                    {!role.editable && <Lock className="h-3 w-3 text-gray-400" />}
                  </button>
                ))}
              </div>

              {/* Permission Grid */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    {activeRole?.label} Permissions
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {activeEditable
                      ? 'Toggle access for each application and permission type'
                      : 'The administrator role always has full access to every application and cannot be changed — so an administrator can never be locked out.'}
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 rounded-lg">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tl-lg w-1/3">
                          Application
                        </th>
                        {matrix.capabilities.map(cap => (
                          <th key={cap} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {cap}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tr-lg">
                          All
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {matrix.apps.map(appId => {
                        const meta = appMeta[appId] || { title: appId, description: '', iconName: 'Shield', color: 'gray' };
                        const appPerms = draft?.[activeRoleTab]?.[appId] || {};
                        const supported = matrix.supported?.[appId] || [];
                        const allGranted = supported.length > 0 &&
                          supported.every(cap => appPerms[cap]);
                        const IconComp = getIcon(meta.iconName);
                        return (
                          <tr key={appId} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-9 h-9 rounded-lg bg-${meta.color}-100 flex items-center justify-center shrink-0`}>
                                  <IconComp className={`h-5 w-5 text-${meta.color}-600`} />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{meta.title}</div>
                                  <div className="text-xs text-gray-500">{meta.description}</div>
                                </div>
                              </div>
                            </td>
                            {matrix.capabilities.map(cap => {
                              const isSupported = supported.includes(cap);
                              const granted = !!appPerms[cap];
                              // Unsupported cells render disabled and dim —
                              // the capability doesn't exist for this app
                              // (e.g. ticket attachments are read-only).
                              if (!isSupported) {
                                return (
                                  <td key={cap} className="px-4 py-4 text-center">
                                    <button
                                      disabled
                                      title={`${cap} is not supported for ${meta.title}`}
                                      className="w-8 h-8 rounded-full flex items-center justify-center mx-auto bg-gray-50 text-gray-300 cursor-not-allowed"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </td>
                                );
                              }
                              return (
                                <td key={cap} className="px-4 py-4 text-center">
                                  <button
                                    disabled={!activeEditable}
                                    onClick={() => togglePermission(activeRoleTab, appId, cap)}
                                    className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto transition-all duration-200 ${
                                      granted
                                        ? 'bg-green-500 text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-400'
                                    } ${activeEditable ? (granted ? 'hover:bg-green-600' : 'hover:bg-gray-200') : 'cursor-not-allowed'}`}
                                    title={activeEditable
                                      ? `${granted ? 'Revoke' : 'Grant'} ${cap} for ${meta.title}`
                                      : 'Administrator permissions are locked'}
                                  >
                                    {granted
                                      ? <Check className="h-4 w-4" />
                                      : <X className="h-4 w-4" />
                                    }
                                  </button>
                                </td>
                              );
                            })}
                            <td className="px-4 py-4 text-center">
                              <button
                                disabled={!activeEditable}
                                onClick={() => {
                                  supported.forEach(cap => {
                                    if (!!appPerms[cap] === allGranted) {
                                      togglePermission(activeRoleTab, appId, cap);
                                    }
                                  });
                                }}
                                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                                  !activeEditable
                                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                    : allGranted
                                      ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                              >
                                {allGranted ? 'Revoke All' : 'Grant All'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {/* Clinical catalogues are not a matrix row — a fixed
                          administrator-only rule stated for clarity. */}
                      {matrix.clinical_config && (
                        <tr className="bg-gray-50/60">
                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                                <Lock className="h-5 w-5 text-red-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {matrix.clinical_config.label}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Administrator-only — cannot be granted to other roles
                                </div>
                              </div>
                            </div>
                          </td>
                          <td colSpan={matrix.capabilities.length + 1} className="px-4 py-4 text-center">
                            <span className="text-xs text-gray-500 italic">
                              Not configurable in this matrix
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="mt-6 flex items-center space-x-6 text-sm text-gray-500">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span>Access granted</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <X className="h-3 w-3 text-gray-400" />
                    </div>
                    <span>Access denied</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center">
                      <X className="h-3 w-3 text-gray-300" />
                    </div>
                    <span>Not supported for this app</span>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 shrink-0">
                <div className="text-sm">
                  {saveError && <span className="text-red-600">{saveError}</span>}
                  {saveOk && !saveError && (
                    <span className="text-green-600">Permissions saved.</span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="flex items-center space-x-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    <span>{saving ? 'Saving…' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPortal;
